import { useEffect, useState } from "react";
import {
    ListContainer,
    ListContext,
    PAGE_DEFAULT,
    PAGE_SIZE_UNLIMITED,
    Page,
    PageRoute,
    SearchBar,
    buildPath,
    buildTemplatePath,
    pn,
} from "@compass/core";
import { ListFooter } from "components";
import { DisciplineListDto, DisciplineListResource, DisciplineListFilter, Permission } from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { DisciplineListPageHeader } from "./DisciplineListPageHeader";
import { DisciplineTable } from "./DisciplineTable";

const routeName = "disciplines";

export const disciplineListPageRoute: PageRoute = {
    handle: "Disciplines",
    templatePath: buildTemplatePath(routeName),
    getPath: () => buildPath(routeName),
};

export const DisciplineListPage = () => {
    const ROOT_PERMISSION: Permission = Permission.SystemUtility;
    const [listItems, setListItems] = useState<DisciplineListDto[]>([]);

    const { listFilter, setListFilter } = useInitListFilter<DisciplineListFilter>({
        sortObjects: [{ property: pn<DisciplineListDto>("name"), isDesc: false }],
    });

    const [total, setTotal] = useState<number>();
    const [resource, setResource] = useState<DisciplineListResource>({});
    const [searchFocused, setSearchFocused] = useState<boolean>(false);

    const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
        listFilter: listFilter,
        setListFilter: setListFilter,
        fetchList: (listFilter) => {
            ApiService.disciplines.getList(listFilter).then((response) => {
                setListItems(response);
            });
        },
        fetchTotal: (listFilter) => {
            setTotal(undefined);
            ApiService.disciplines.getListTotal(listFilter).then((response) => {
                setTotal(response);
            });
        },
    });

    useEffect(() => {
        loadList(listFilter);
        ApiService.disciplines.getListResource().then((response) => {
            setResource(response);
        });
    }, []);

    return (
        <>
            <Page header={<DisciplineListPageHeader rootPermission={ROOT_PERMISSION} handle={disciplineListPageRoute.handle} />}>
                <ListContainer
                    fullHeight
                    header={<SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />}
                    tableContainer={
                        <DisciplineTable
                            rootPermission={ROOT_PERMISSION}
                            tableContainerRef={tableContainerRef}
                            disciplines={listItems}
                            setDisciplines={setListItems}
                            resource={resource}
                            searchFocused={searchFocused}
                            sortObjects={listFilter.sortObjects}
                            sort={applySort}
                            loadList={() => loadList(listFilter)}
                        />
                    }
                    footer={
                        <ListFooter
                            total={total}
                            size={listFilter.pageSize}
                            page={listFilter.page}
                            onPageChange={applyPage}
                            onSizeChange={applyPageSize}
                            onSortMethodChange={applySortMethod}
                        />
                    }
                />
            </Page>
            <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
                <Outlet />
            </ListContext.Provider>
        </>
    );
};